import { isOfferedCurrency } from './guest-currency';
import { currencies } from './currency-data';

/**
 * Display-currency rates on the server, so a page arrives priced in the
 * visitor's currency instead of in AUD and then switching once the browser has
 * fetched rates of its own (the switch every visitor outside Australia saw).
 *
 * The rates only drive DISPLAY conversion. Checkout is quoted by the server at
 * its own rate (functions/src/lib/checkout-fx.ts), so an hour-old figure here
 * can never change what anyone is charged.
 *
 * Pure; the cached feed read is src/lib/display-rates-server.ts.
 */

/** Rates relative to AUD: 1 AUD = rates[code]. */
export type DisplayRates = Record<string, number>;

/** Stored amounts are AUD; the base needs no rate. Spelled here rather than imported from the client-only theme context. */
const BASE = 'AUD';

/**
 * Keeps only ISO-shaped codes with a usable rate. The table is handed straight
 * to every browser's formatter, so a malformed entry is dropped here rather
 * than rendered as NaN or a 1:1 figure under a foreign symbol.
 */
export function usableRates(input: unknown): DisplayRates | null {
  if (!input || typeof input !== 'object') return null;
  const rates: DisplayRates = {};
  for (const [code, value] of Object.entries(input as Record<string, unknown>)) {
    if (/^[A-Z]{3}$/.test(code) && typeof value === 'number' && Number.isFinite(value) && value > 0) {
      rates[code] = value;
    }
  }
  if (Object.keys(rates).length === 0) return null;
  // The base is 1 by definition; pinned so a feed glitch cannot rescale it.
  rates[BASE] = 1;
  return rates;
}

/** Whether `rates` holds a rate `code` can be converted at. */
export function hasUsableRate(rates: DisplayRates | null | undefined, code: string): boolean {
  const rate = rates?.[code];
  return typeof rate === 'number' && Number.isFinite(rate) && rate > 0;
}

/**
 * The slice of the feed a picker can show. The feed carries some 160
 * currencies and this table rides in every page, so it carries the 29 a
 * visitor can choose. A saved currency outside them (an old account's) has no
 * rate here, and the theme context then fetches the full table as it always did.
 */
export function offeredRates(rates: DisplayRates): DisplayRates {
  const out: DisplayRates = { [BASE]: 1 };
  for (const { code } of currencies) {
    if (code !== BASE && hasUsableRate(rates, code)) out[code] = rates[code];
  }
  return out;
}

/**
 * The currency the server renders prices in, which the browser's first render
 * must repeat exactly: the visitor's IP country's, when it is a currency a
 * picker offers and the server has its rate; otherwise AUD, which is what every
 * page rendered in before.
 */
export function serverDisplayCurrency(rates: DisplayRates | null, geoCurrency: string | null): string {
  if (!rates || !geoCurrency || !isOfferedCurrency(geoCurrency) || !hasUsableRate(rates, geoCurrency)) return BASE;
  return geoCurrency;
}

/**
 * A signed-out visitor's detected currency: the server's IP-country answer
 * first, then the browser's own guesses (the `gc` cookie, the timezone, the
 * language). The cookie holds the same IP answer from an earlier visit, but a
 * first visit has none yet, and a timezone that disagrees with the IP would
 * switch the server-rendered prices to another currency after hydration.
 */
export function preferGeoCurrency(geoCurrency: string | null, guess: () => string): string {
  return isOfferedCurrency(geoCurrency) ? geoCurrency : guess();
}

/**
 * Wrap a load for a RENDER path: wait at most `budgetMs` for it, answer null
 * past that or on failure, and after a failure answer null at once for
 * `backoffMs`, so an outage of the source costs each page nothing rather than
 * the budget. A load still running when its budget runs out carries on (and
 * fills its cache for the next request); if it fails later, that failure
 * starts the back-off too. Never rejects.
 */
export function renderBudgeted<T>(
  load: () => Promise<T>,
  options: { budgetMs: number; backoffMs: number; now?: () => number; onError?: (err: unknown) => void },
): () => Promise<T | null> {
  const now = options.now ?? Date.now;
  let failedAt = Number.NEGATIVE_INFINITY;
  const fail = (err: unknown) => {
    failedAt = now();
    options.onError?.(err);
  };
  return async () => {
    if (now() - failedAt < options.backoffMs) return null;
    let attempt: Promise<T>;
    try {
      attempt = load();
    } catch (err) {
      fail(err);
      return null;
    }
    const settled = attempt.then(
      (value): T | null => value,
      (err): null => { fail(err); return null; },
    );
    let timer: ReturnType<typeof setTimeout> | undefined;
    const budget = new Promise<null>((resolve) => { timer = setTimeout(() => resolve(null), options.budgetMs); });
    try {
      return await Promise.race([settled, budget]);
    } finally {
      clearTimeout(timer);
    }
  };
}
